import { supabase } from '@/integrations/supabase/client';
import { reportError } from '@/lib/error-reporting';
import type { Profile } from '@/types/profile';

export interface ExtractedCareerContext {
  job_role?: string | null;
  target_job_role?: string | null;
  industry?: string | null;
  career_narrative?: string | null;
}

/**
 * Save career context extracted from AI values assessment to the user's profile.
 *
 * Guards:
 * - Only updates job_role, target_job_role, industry if currently null on profile
 *   (doesn't overwrite manual edits from Settings).
 * - Only updates career_context if the new narrative is non-empty AND at least
 *   as long as the existing value (prevents data regression from weaker extractions).
 *
 * Fire-and-forget — errors are logged via reportError, never thrown.
 */
export async function saveCareerContextFromExtraction(
  userId: string,
  profile: Profile | null,
  careerContext: ExtractedCareerContext | undefined
): Promise<boolean> {
  if (!careerContext || !userId) return false;

  const updates: Record<string, string> = {};

  // Only fill in fields that are currently empty on the profile
  if (!profile?.job_role && careerContext.job_role) {
    updates.job_role = careerContext.job_role;
  }
  if (!profile?.target_job_role && careerContext.target_job_role) {
    updates.target_job_role = careerContext.target_job_role;
  }
  if (!profile?.industry && careerContext.industry) {
    updates.industry = careerContext.industry;
  }

  // Career context overwrite guard: only update if new value is non-empty
  // and at least as long as the existing value
  const newNarrative = careerContext.career_narrative?.trim();
  if (newNarrative) {
    const existingLength = profile?.career_context?.length ?? 0;
    if (newNarrative.length >= existingLength) {
      updates.career_context = newNarrative;
    }
  }

  if (Object.keys(updates).length === 0) return false;

  try {
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('user_id', userId);

    if (error) throw error;
    return true;
  } catch (error) {
    reportError(error, 'values-assessment', { action: 'save-career-context' });
    return false;
  }
}
